/**
 * Built-in Plugins Library
 * Concrete plugin implementations for the VST-style plugin system
 */

import {
  Plugin,
  PluginParameter,
  PLUGIN_TYPES,
  PLUGIN_CATEGORIES,
  PARAM_TYPES,
  pluginRegistry,
} from './PluginSystem';

// ==================== COMPRESSOR PLUGIN ====================

export class CompressorPlugin extends Plugin {
  constructor(audioContext, options = {}) {
    super(audioContext, {
      name: 'Compressor',
      type: PLUGIN_TYPES.EFFECT,
      category: PLUGIN_CATEGORIES.DYNAMICS,
      version: '1.0.0',
      author: 'String Sauce',
      description: 'Dynamic range compressor with threshold, ratio, attack, and release controls',
      ...options,
    });
  }

  _initializeNodes() {
    this.inputNode = this.audioContext.createGain();
    this.outputNode = this.audioContext.createGain();
    this.bypassNode = this.audioContext.createGain();
    
    // Create compressor node
    this.compressor = this.audioContext.createDynamicsCompressor();
    
    // Create makeup gain
    this.makeupGain = this.audioContext.createGain();
    
    // Signal path: input -> compressor -> makeup -> output
    this.inputNode.connect(this.compressor);
    this.compressor.connect(this.makeupGain);
    this.makeupGain.connect(this.outputNode);
    
    // Bypass path
    this.inputNode.connect(this.bypassNode);
    this.bypassNode.gain.value = 0;
    this.bypassNode.connect(this.outputNode);
  }

  _initializeParameters() {
    this.addParameter(new PluginParameter({
      id: 'threshold',
      name: 'Threshold',
      type: PARAM_TYPES.FLOAT,
      defaultValue: -24,
      minValue: -60,
      maxValue: 0,
      unit: 'dB',
    }));

    this.addParameter(new PluginParameter({
      id: 'ratio',
      name: 'Ratio',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 4,
      minValue: 1,
      maxValue: 20,
      unit: ':1',
    }));

    this.addParameter(new PluginParameter({
      id: 'attack',
      name: 'Attack',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0.003,
      minValue: 0,
      maxValue: 1,
      unit: 's',
      displayValue: (v) => `${(v * 1000).toFixed(1)}ms`,
    }));

    this.addParameter(new PluginParameter({
      id: 'release',
      name: 'Release',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0.25,
      minValue: 0.01,
      maxValue: 1,
      unit: 's',
      displayValue: (v) => `${(v * 1000).toFixed(0)}ms`,
    }));

    this.addParameter(new PluginParameter({
      id: 'knee',
      name: 'Knee',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 6,
      minValue: 0,
      maxValue: 40,
      unit: 'dB',
    }));

    this.addParameter(new PluginParameter({
      id: 'makeup',
      name: 'Makeup',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0,
      minValue: -12,
      maxValue: 24,
      unit: 'dB',
    }));

    this.addParameter(new PluginParameter({
      id: 'mix',
      name: 'Mix',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 1,
      minValue: 0,
      maxValue: 1,
      displayValue: (v) => `${Math.round(v * 100)}%`,
    }));

    // Initialize factory presets
    this.presets = [
      { id: 'gentle', name: 'Gentle', parameters: { threshold: -20, ratio: 2, attack: 0.01, release: 0.3, knee: 10, makeup: 2, mix: 1 } },
      { id: 'vocal', name: 'Vocal', parameters: { threshold: -18, ratio: 3, attack: 0.005, release: 0.2, knee: 6, makeup: 4, mix: 1 } },
      { id: 'drums', name: 'Drums', parameters: { threshold: -12, ratio: 6, attack: 0.001, release: 0.1, knee: 3, makeup: 6, mix: 1 } },
      { id: 'bus', name: 'Bus Glue', parameters: { threshold: -24, ratio: 2, attack: 0.03, release: 0.4, knee: 12, makeup: 2, mix: 0.5 } },
      { id: 'limiter', name: 'Limiter', parameters: { threshold: -6, ratio: 20, attack: 0.001, release: 0.05, knee: 0, makeup: 0, mix: 1 } },
    ];
  }

  onParameterChange(paramId, value) {
    switch (paramId) {
      case 'threshold':
        this.compressor.threshold.setValueAtTime(value, this.audioContext.currentTime);
        break;
      case 'ratio':
        this.compressor.ratio.setValueAtTime(value, this.audioContext.currentTime);
        break;
      case 'attack':
        this.compressor.attack.setValueAtTime(value, this.audioContext.currentTime);
        break;
      case 'release':
        this.compressor.release.setValueAtTime(value, this.audioContext.currentTime);
        break;
      case 'knee':
        this.compressor.knee.setValueAtTime(value, this.audioContext.currentTime);
        break;
      case 'makeup':
        const gain = Math.pow(10, value / 20);
        this.makeupGain.gain.setValueAtTime(gain, this.audioContext.currentTime);
        break;
      default:
        break;
    }
  }

  _updateBypass() {
    const currentTime = this.audioContext.currentTime;
    if (this.enabled) {
      this.bypassNode.gain.setValueAtTime(0, currentTime);
      this.makeupGain.gain.setValueAtTime(
        Math.pow(10, this.getParameter('makeup').getValue() / 20),
        currentTime
      );
    } else {
      this.bypassNode.gain.setValueAtTime(1, currentTime);
      this.makeupGain.gain.setValueAtTime(0, currentTime);
    }
  }

  // Get current gain reduction (for metering)
  getGainReduction() {
    return this.compressor.reduction;
  }
}

// ==================== PARAMETRIC EQ PLUGIN ====================

export class ParametricEQPlugin extends Plugin {
  constructor(audioContext, options = {}) {
    super(audioContext, {
      name: 'Parametric EQ',
      type: PLUGIN_TYPES.EFFECT,
      category: PLUGIN_CATEGORIES.EQ,
      version: '1.0.0',
      author: 'String Sauce',
      description: '4-band parametric equalizer with low shelf, 2 peaking bands, and high shelf',
      ...options,
    });
  }

  _initializeNodes() {
    this.inputNode = this.audioContext.createGain();
    this.outputNode = this.audioContext.createGain();
    this.bypassNode = this.audioContext.createGain();
    
    // Create 4 filter bands
    this.lowShelf = this.audioContext.createBiquadFilter();
    this.lowShelf.type = 'lowshelf';
    
    this.mid1 = this.audioContext.createBiquadFilter();
    this.mid1.type = 'peaking';
    
    this.mid2 = this.audioContext.createBiquadFilter();
    this.mid2.type = 'peaking';
    
    this.highShelf = this.audioContext.createBiquadFilter();
    this.highShelf.type = 'highshelf';
    
    // Signal path
    this.inputNode.connect(this.lowShelf);
    this.lowShelf.connect(this.mid1);
    this.mid1.connect(this.mid2);
    this.mid2.connect(this.highShelf);
    this.highShelf.connect(this.outputNode);
    
    // Bypass path
    this.inputNode.connect(this.bypassNode);
    this.bypassNode.gain.value = 0;
    this.bypassNode.connect(this.outputNode);
  }

  _initializeParameters() {
    // Low Shelf
    this.addParameter(new PluginParameter({
      id: 'lowFreq',
      name: 'Low Freq',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 100,
      minValue: 20,
      maxValue: 500,
      unit: 'Hz',
    }));
    this.addParameter(new PluginParameter({
      id: 'lowGain',
      name: 'Low Gain',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0,
      minValue: -18,
      maxValue: 18,
      unit: 'dB',
    }));

    // Mid 1 (Low-Mid)
    this.addParameter(new PluginParameter({
      id: 'mid1Freq',
      name: 'Mid 1 Freq',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 400,
      minValue: 100,
      maxValue: 2000,
      unit: 'Hz',
    }));
    this.addParameter(new PluginParameter({
      id: 'mid1Gain',
      name: 'Mid 1 Gain',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0,
      minValue: -18,
      maxValue: 18,
      unit: 'dB',
    }));
    this.addParameter(new PluginParameter({
      id: 'mid1Q',
      name: 'Mid 1 Q',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 1,
      minValue: 0.1,
      maxValue: 10,
    }));

    // Mid 2 (High-Mid)
    this.addParameter(new PluginParameter({
      id: 'mid2Freq',
      name: 'Mid 2 Freq',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 2500,
      minValue: 500,
      maxValue: 8000,
      unit: 'Hz',
    }));
    this.addParameter(new PluginParameter({
      id: 'mid2Gain',
      name: 'Mid 2 Gain',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0,
      minValue: -18,
      maxValue: 18,
      unit: 'dB',
    }));
    this.addParameter(new PluginParameter({
      id: 'mid2Q',
      name: 'Mid 2 Q',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 1,
      minValue: 0.1,
      maxValue: 10,
    }));

    // High Shelf
    this.addParameter(new PluginParameter({
      id: 'highFreq',
      name: 'High Freq',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 8000,
      minValue: 2000,
      maxValue: 16000,
      unit: 'Hz',
    }));
    this.addParameter(new PluginParameter({
      id: 'highGain',
      name: 'High Gain',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0,
      minValue: -18,
      maxValue: 18,
      unit: 'dB',
    }));

    // Factory presets
    this.presets = [
      { id: 'flat', name: 'Flat', parameters: { lowGain: 0, mid1Gain: 0, mid2Gain: 0, highGain: 0 } },
      { id: 'warmth', name: 'Warmth', parameters: { lowFreq: 120, lowGain: 3, mid1Freq: 300, mid1Gain: -2, mid2Gain: 0, highGain: -1 } },
      { id: 'presence', name: 'Presence', parameters: { lowGain: 0, mid1Gain: -2, mid2Freq: 3000, mid2Gain: 3, highGain: 2 } },
      { id: 'scooped', name: 'Scooped', parameters: { lowGain: 4, mid1Freq: 500, mid1Gain: -4, mid2Freq: 2000, mid2Gain: -3, highGain: 4 } },
      { id: 'telephone', name: 'Telephone', parameters: { lowFreq: 300, lowGain: -18, mid1Gain: 0, mid2Gain: 0, highFreq: 3000, highGain: -18 } },
    ];
  }

  onParameterChange(paramId, value) {
    const currentTime = this.audioContext.currentTime;
    switch (paramId) {
      case 'lowFreq':
        this.lowShelf.frequency.setValueAtTime(value, currentTime);
        break;
      case 'lowGain':
        this.lowShelf.gain.setValueAtTime(value, currentTime);
        break;
      case 'mid1Freq':
        this.mid1.frequency.setValueAtTime(value, currentTime);
        break;
      case 'mid1Gain':
        this.mid1.gain.setValueAtTime(value, currentTime);
        break;
      case 'mid1Q':
        this.mid1.Q.setValueAtTime(value, currentTime);
        break;
      case 'mid2Freq':
        this.mid2.frequency.setValueAtTime(value, currentTime);
        break;
      case 'mid2Gain':
        this.mid2.gain.setValueAtTime(value, currentTime);
        break;
      case 'mid2Q':
        this.mid2.Q.setValueAtTime(value, currentTime);
        break;
      case 'highFreq':
        this.highShelf.frequency.setValueAtTime(value, currentTime);
        break;
      case 'highGain':
        this.highShelf.gain.setValueAtTime(value, currentTime);
        break;
      default:
        break;
    }
  }

  _updateBypass() {
    const currentTime = this.audioContext.currentTime;
    if (this.enabled) {
      this.bypassNode.gain.setValueAtTime(0, currentTime);
    } else {
      this.bypassNode.gain.setValueAtTime(1, currentTime);
      // Reset all gains to 0 when bypassed
      this.lowShelf.gain.setValueAtTime(0, currentTime);
      this.mid1.gain.setValueAtTime(0, currentTime);
      this.mid2.gain.setValueAtTime(0, currentTime);
      this.highShelf.gain.setValueAtTime(0, currentTime);
    }
  }
}

// ==================== REVERB PLUGIN ====================

export class ReverbPlugin extends Plugin {
  constructor(audioContext, options = {}) {
    super(audioContext, {
      name: 'Reverb',
      type: PLUGIN_TYPES.EFFECT,
      category: PLUGIN_CATEGORIES.REVERB,
      version: '1.0.0',
      author: 'String Sauce',
      description: 'Algorithmic reverb with decay, pre-delay, and damping controls',
      ...options,
    });
    
    this._generateImpulse();
  }

  _initializeNodes() {
    this.inputNode = this.audioContext.createGain();
    this.outputNode = this.audioContext.createGain();
    this.bypassNode = this.audioContext.createGain();
    
    // Dry/wet paths
    this.dryGain = this.audioContext.createGain();
    this.wetGain = this.audioContext.createGain();
    
    // Pre-delay
    this.preDelay = this.audioContext.createDelay(1);
    
    // Convolver for reverb
    this.convolver = this.audioContext.createConvolver();
    
    // Damping filter (low-pass on wet signal)
    this.dampingFilter = this.audioContext.createBiquadFilter();
    this.dampingFilter.type = 'lowpass';
    this.dampingFilter.frequency.value = 8000;
    
    // Signal routing
    // Dry path
    this.inputNode.connect(this.dryGain);
    this.dryGain.connect(this.outputNode);
    
    // Wet path: input -> preDelay -> convolver -> damping -> wetGain -> output
    this.inputNode.connect(this.preDelay);
    this.preDelay.connect(this.convolver);
    this.convolver.connect(this.dampingFilter);
    this.dampingFilter.connect(this.wetGain);
    this.wetGain.connect(this.outputNode);
    
    // Bypass path
    this.inputNode.connect(this.bypassNode);
    this.bypassNode.gain.value = 0;
    this.bypassNode.connect(this.outputNode);
    
    // Default wet/dry
    this.dryGain.gain.value = 0.7;
    this.wetGain.gain.value = 0.3;
  }

  _initializeParameters() {
    this.addParameter(new PluginParameter({
      id: 'decay',
      name: 'Decay',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 2,
      minValue: 0.1,
      maxValue: 10,
      unit: 's',
    }));

    this.addParameter(new PluginParameter({
      id: 'preDelay',
      name: 'Pre-Delay',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0.02,
      minValue: 0,
      maxValue: 0.2,
      unit: 's',
      displayValue: (v) => `${(v * 1000).toFixed(0)}ms`,
    }));

    this.addParameter(new PluginParameter({
      id: 'damping',
      name: 'Damping',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 8000,
      minValue: 1000,
      maxValue: 16000,
      unit: 'Hz',
      displayValue: (v) => `${(v / 1000).toFixed(1)}kHz`,
    }));

    this.addParameter(new PluginParameter({
      id: 'size',
      name: 'Size',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0.5,
      minValue: 0.1,
      maxValue: 1,
      displayValue: (v) => ['Small', 'Medium', 'Large'][Math.floor(v * 2.99)] || 'Medium',
    }));

    this.addParameter(new PluginParameter({
      id: 'mix',
      name: 'Mix',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0.3,
      minValue: 0,
      maxValue: 1,
      displayValue: (v) => `${Math.round(v * 100)}%`,
    }));

    // Factory presets
    this.presets = [
      { id: 'room', name: 'Room', parameters: { decay: 0.5, preDelay: 0.01, damping: 8000, size: 0.3, mix: 0.2 } },
      { id: 'hall', name: 'Hall', parameters: { decay: 2.5, preDelay: 0.03, damping: 6000, size: 0.7, mix: 0.3 } },
      { id: 'plate', name: 'Plate', parameters: { decay: 1.5, preDelay: 0, damping: 10000, size: 0.5, mix: 0.35 } },
      { id: 'cathedral', name: 'Cathedral', parameters: { decay: 5, preDelay: 0.05, damping: 4000, size: 1, mix: 0.4 } },
      { id: 'ambient', name: 'Ambient', parameters: { decay: 8, preDelay: 0.1, damping: 3000, size: 0.9, mix: 0.5 } },
    ];
  }

  _generateImpulse() {
    const decay = this.parameters.get('decay')?.getValue() || 2;
    const size = this.parameters.get('size')?.getValue() || 0.5;
    
    const sampleRate = this.audioContext.sampleRate;
    const length = sampleRate * decay;
    const impulse = this.audioContext.createBuffer(2, length, sampleRate);
    
    for (let channel = 0; channel < 2; channel++) {
      const data = impulse.getChannelData(channel);
      for (let i = 0; i < length; i++) {
        // Exponential decay with random noise
        const t = i / sampleRate;
        const envelope = Math.exp(-3 * t / decay);
        // Add early reflections based on size
        const earlyReflections = i < sampleRate * 0.05 * size 
          ? Math.random() * 0.5 
          : 0;
        data[i] = ((Math.random() * 2 - 1) * envelope + earlyReflections) * 0.5;
      }
    }
    
    this.convolver.buffer = impulse;
  }

  onParameterChange(paramId, value) {
    const currentTime = this.audioContext.currentTime;
    switch (paramId) {
      case 'decay':
      case 'size':
        // Regenerate impulse response
        this._generateImpulse();
        break;
      case 'preDelay':
        this.preDelay.delayTime.setValueAtTime(value, currentTime);
        break;
      case 'damping':
        this.dampingFilter.frequency.setValueAtTime(value, currentTime);
        break;
      case 'mix':
        this.dryGain.gain.setValueAtTime(1 - value, currentTime);
        this.wetGain.gain.setValueAtTime(value, currentTime);
        break;
      default:
        break;
    }
  }

  _updateBypass() {
    const currentTime = this.audioContext.currentTime;
    if (this.enabled) {
      this.bypassNode.gain.setValueAtTime(0, currentTime);
      const mix = this.getParameter('mix').getValue();
      this.dryGain.gain.setValueAtTime(1 - mix, currentTime);
      this.wetGain.gain.setValueAtTime(mix, currentTime);
    } else {
      this.bypassNode.gain.setValueAtTime(1, currentTime);
      this.dryGain.gain.setValueAtTime(0, currentTime);
      this.wetGain.gain.setValueAtTime(0, currentTime);
    }
  }
}

// ==================== DELAY PLUGIN ====================

export class DelayPlugin extends Plugin {
  constructor(audioContext, options = {}) {
    super(audioContext, {
      name: 'Delay',
      type: PLUGIN_TYPES.EFFECT,
      category: PLUGIN_CATEGORIES.DELAY,
      version: '1.0.0',
      author: 'String Sauce',
      description: 'Stereo delay with ping-pong mode, tempo sync, and modulation',
      ...options,
    });
  }

  _initializeNodes() {
    this.inputNode = this.audioContext.createGain();
    this.outputNode = this.audioContext.createGain();
    this.bypassNode = this.audioContext.createGain();
    
    // Dry/wet
    this.dryGain = this.audioContext.createGain();
    this.wetGain = this.audioContext.createGain();
    
    // Left and right delays
    this.delayL = this.audioContext.createDelay(5);
    this.delayR = this.audioContext.createDelay(5);
    
    // Feedback
    this.feedbackL = this.audioContext.createGain();
    this.feedbackR = this.audioContext.createGain();
    
    // Delay filter (for analog warmth)
    this.filterL = this.audioContext.createBiquadFilter();
    this.filterL.type = 'lowpass';
    this.filterL.frequency.value = 6000;
    
    this.filterR = this.audioContext.createBiquadFilter();
    this.filterR.type = 'lowpass';
    this.filterR.frequency.value = 6000;
    
    // Channel splitter/merger
    this.splitter = this.audioContext.createChannelSplitter(2);
    this.merger = this.audioContext.createChannelMerger(2);
    
    // Dry path
    this.inputNode.connect(this.dryGain);
    this.dryGain.connect(this.outputNode);
    
    // Wet path (stereo)
    this.inputNode.connect(this.splitter);
    
    // Left channel
    this.splitter.connect(this.delayL, 0);
    this.delayL.connect(this.filterL);
    this.filterL.connect(this.feedbackL);
    this.feedbackL.connect(this.delayL); // Feedback loop
    this.filterL.connect(this.merger, 0, 0);
    
    // Right channel
    this.splitter.connect(this.delayR, 1);
    this.delayR.connect(this.filterR);
    this.filterR.connect(this.feedbackR);
    this.feedbackR.connect(this.delayR); // Feedback loop
    this.filterR.connect(this.merger, 0, 1);
    
    this.merger.connect(this.wetGain);
    this.wetGain.connect(this.outputNode);
    
    // Bypass
    this.inputNode.connect(this.bypassNode);
    this.bypassNode.gain.value = 0;
    this.bypassNode.connect(this.outputNode);
    
    // Default values
    this.dryGain.gain.value = 0.7;
    this.wetGain.gain.value = 0.3;
    this.feedbackL.gain.value = 0.4;
    this.feedbackR.gain.value = 0.4;
  }

  _initializeParameters() {
    this.addParameter(new PluginParameter({
      id: 'time',
      name: 'Time',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0.3,
      minValue: 0.01,
      maxValue: 2,
      unit: 's',
      displayValue: (v) => v < 1 ? `${(v * 1000).toFixed(0)}ms` : `${v.toFixed(2)}s`,
    }));

    this.addParameter(new PluginParameter({
      id: 'feedback',
      name: 'Feedback',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0.4,
      minValue: 0,
      maxValue: 0.95,
      displayValue: (v) => `${Math.round(v * 100)}%`,
    }));

    this.addParameter(new PluginParameter({
      id: 'stereoSpread',
      name: 'Stereo Spread',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0,
      minValue: -0.5,
      maxValue: 0.5,
      displayValue: (v) => v === 0 ? 'Mono' : `${(v * 200).toFixed(0)}%`,
    }));

    this.addParameter(new PluginParameter({
      id: 'filter',
      name: 'Filter',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 6000,
      minValue: 500,
      maxValue: 12000,
      unit: 'Hz',
      displayValue: (v) => `${(v / 1000).toFixed(1)}kHz`,
    }));

    this.addParameter(new PluginParameter({
      id: 'mix',
      name: 'Mix',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0.3,
      minValue: 0,
      maxValue: 1,
      displayValue: (v) => `${Math.round(v * 100)}%`,
    }));

    // Factory presets
    this.presets = [
      { id: 'slapback', name: 'Slapback', parameters: { time: 0.08, feedback: 0.1, stereoSpread: 0, filter: 8000, mix: 0.3 } },
      { id: 'quarter', name: 'Quarter Note', parameters: { time: 0.5, feedback: 0.4, stereoSpread: 0, filter: 6000, mix: 0.25 } },
      { id: 'pingpong', name: 'Ping Pong', parameters: { time: 0.3, feedback: 0.5, stereoSpread: 0.5, filter: 5000, mix: 0.35 } },
      { id: 'ambient', name: 'Ambient', parameters: { time: 0.8, feedback: 0.6, stereoSpread: 0.3, filter: 3000, mix: 0.4 } },
      { id: 'dub', name: 'Dub', parameters: { time: 0.375, feedback: 0.7, stereoSpread: 0.2, filter: 2000, mix: 0.5 } },
    ];
  }

  onParameterChange(paramId, value) {
    const currentTime = this.audioContext.currentTime;
    switch (paramId) {
      case 'time':
        const spread = this.getParameter('stereoSpread').getValue();
        this.delayL.delayTime.setValueAtTime(value * (1 - spread), currentTime);
        this.delayR.delayTime.setValueAtTime(value * (1 + spread), currentTime);
        break;
      case 'feedback':
        this.feedbackL.gain.setValueAtTime(value, currentTime);
        this.feedbackR.gain.setValueAtTime(value, currentTime);
        break;
      case 'stereoSpread':
        const time = this.getParameter('time').getValue();
        this.delayL.delayTime.setValueAtTime(time * (1 - value), currentTime);
        this.delayR.delayTime.setValueAtTime(time * (1 + value), currentTime);
        break;
      case 'filter':
        this.filterL.frequency.setValueAtTime(value, currentTime);
        this.filterR.frequency.setValueAtTime(value, currentTime);
        break;
      case 'mix':
        this.dryGain.gain.setValueAtTime(1 - value, currentTime);
        this.wetGain.gain.setValueAtTime(value, currentTime);
        break;
      default:
        break;
    }
  }

  _updateBypass() {
    const currentTime = this.audioContext.currentTime;
    if (this.enabled) {
      this.bypassNode.gain.setValueAtTime(0, currentTime);
      const mix = this.getParameter('mix').getValue();
      this.dryGain.gain.setValueAtTime(1 - mix, currentTime);
      this.wetGain.gain.setValueAtTime(mix, currentTime);
    } else {
      this.bypassNode.gain.setValueAtTime(1, currentTime);
      this.dryGain.gain.setValueAtTime(0, currentTime);
      this.wetGain.gain.setValueAtTime(0, currentTime);
    }
  }
}

// ==================== CHORUS PLUGIN ====================

export class ChorusPlugin extends Plugin {
  constructor(audioContext, options = {}) {
    super(audioContext, {
      name: 'Chorus',
      type: PLUGIN_TYPES.EFFECT,
      category: PLUGIN_CATEGORIES.MODULATION,
      version: '1.0.0',
      author: 'String Sauce',
      description: 'Stereo chorus with rate, depth, and feedback controls',
      ...options,
    });
  }

  _initializeNodes() {
    this.inputNode = this.audioContext.createGain();
    this.outputNode = this.audioContext.createGain();
    this.bypassNode = this.audioContext.createGain();
    
    // Dry/wet
    this.dryGain = this.audioContext.createGain();
    this.wetGain = this.audioContext.createGain();
    
    // Delays for chorus effect
    this.delayL = this.audioContext.createDelay(0.1);
    this.delayR = this.audioContext.createDelay(0.1);
    this.delayL.delayTime.value = 0.003;
    this.delayR.delayTime.value = 0.003;
    
    // LFOs for modulation
    this.lfoL = this.audioContext.createOscillator();
    this.lfoR = this.audioContext.createOscillator();
    this.lfoL.type = 'sine';
    this.lfoR.type = 'sine';
    this.lfoL.frequency.value = 0.5;
    this.lfoR.frequency.value = 0.5;
    
    // LFO gains (depth control)
    this.lfoGainL = this.audioContext.createGain();
    this.lfoGainR = this.audioContext.createGain();
    this.lfoGainL.gain.value = 0.002;
    this.lfoGainR.gain.value = 0.002;
    
    // Connect LFOs to delay time
    this.lfoL.connect(this.lfoGainL);
    this.lfoR.connect(this.lfoGainR);
    this.lfoGainL.connect(this.delayL.delayTime);
    this.lfoGainR.connect(this.delayR.delayTime);
    
    // Phase offset for stereo
    this.lfoR.detune.value = 50; // Slight detuning for stereo spread
    
    // Start LFOs
    this.lfoL.start();
    this.lfoR.start();
    
    // Signal path
    this.inputNode.connect(this.dryGain);
    this.dryGain.connect(this.outputNode);
    
    this.inputNode.connect(this.delayL);
    this.inputNode.connect(this.delayR);
    this.delayL.connect(this.wetGain);
    this.delayR.connect(this.wetGain);
    this.wetGain.connect(this.outputNode);
    
    // Bypass
    this.inputNode.connect(this.bypassNode);
    this.bypassNode.gain.value = 0;
    this.bypassNode.connect(this.outputNode);
    
    this.dryGain.gain.value = 0.5;
    this.wetGain.gain.value = 0.5;
  }

  _initializeParameters() {
    this.addParameter(new PluginParameter({
      id: 'rate',
      name: 'Rate',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0.5,
      minValue: 0.1,
      maxValue: 5,
      unit: 'Hz',
    }));

    this.addParameter(new PluginParameter({
      id: 'depth',
      name: 'Depth',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0.002,
      minValue: 0,
      maxValue: 0.01,
      displayValue: (v) => `${Math.round(v * 10000) / 100}%`,
    }));

    this.addParameter(new PluginParameter({
      id: 'mix',
      name: 'Mix',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0.5,
      minValue: 0,
      maxValue: 1,
      displayValue: (v) => `${Math.round(v * 100)}%`,
    }));

    // Factory presets
    this.presets = [
      { id: 'subtle', name: 'Subtle', parameters: { rate: 0.3, depth: 0.001, mix: 0.3 } },
      { id: 'classic', name: 'Classic', parameters: { rate: 0.5, depth: 0.002, mix: 0.5 } },
      { id: 'deep', name: 'Deep', parameters: { rate: 0.8, depth: 0.005, mix: 0.6 } },
      { id: 'fast', name: 'Fast', parameters: { rate: 3, depth: 0.003, mix: 0.4 } },
      { id: 'extreme', name: 'Extreme', parameters: { rate: 4, depth: 0.008, mix: 0.7 } },
    ];
  }

  onParameterChange(paramId, value) {
    const currentTime = this.audioContext.currentTime;
    switch (paramId) {
      case 'rate':
        this.lfoL.frequency.setValueAtTime(value, currentTime);
        this.lfoR.frequency.setValueAtTime(value, currentTime);
        break;
      case 'depth':
        this.lfoGainL.gain.setValueAtTime(value, currentTime);
        this.lfoGainR.gain.setValueAtTime(value, currentTime);
        break;
      case 'mix':
        this.dryGain.gain.setValueAtTime(1 - value * 0.5, currentTime);
        this.wetGain.gain.setValueAtTime(value, currentTime);
        break;
      default:
        break;
    }
  }

  _updateBypass() {
    const currentTime = this.audioContext.currentTime;
    if (this.enabled) {
      this.bypassNode.gain.setValueAtTime(0, currentTime);
      const mix = this.getParameter('mix').getValue();
      this.dryGain.gain.setValueAtTime(1 - mix * 0.5, currentTime);
      this.wetGain.gain.setValueAtTime(mix, currentTime);
    } else {
      this.bypassNode.gain.setValueAtTime(1, currentTime);
      this.dryGain.gain.setValueAtTime(0, currentTime);
      this.wetGain.gain.setValueAtTime(0, currentTime);
    }
  }
}

// ==================== GAIN/UTILITY PLUGIN ====================

export class GainPlugin extends Plugin {
  constructor(audioContext, options = {}) {
    super(audioContext, {
      name: 'Gain',
      type: PLUGIN_TYPES.EFFECT,
      category: PLUGIN_CATEGORIES.UTILITY,
      version: '1.0.0',
      author: 'String Sauce',
      description: 'Simple gain stage with pan and phase invert',
      ...options,
    });
  }

  _initializeNodes() {
    this.inputNode = this.audioContext.createGain();
    this.outputNode = this.audioContext.createGain();
    this.bypassNode = this.audioContext.createGain();
    
    // Main gain
    this.gainNode = this.audioContext.createGain();
    
    // Panner
    this.panner = this.audioContext.createStereoPanner();
    
    // Signal path
    this.inputNode.connect(this.gainNode);
    this.gainNode.connect(this.panner);
    this.panner.connect(this.outputNode);
    
    // Bypass
    this.inputNode.connect(this.bypassNode);
    this.bypassNode.gain.value = 0;
    this.bypassNode.connect(this.outputNode);
  }

  _initializeParameters() {
    this.addParameter(new PluginParameter({
      id: 'gain',
      name: 'Gain',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0,
      minValue: -48,
      maxValue: 24,
      unit: 'dB',
    }));

    this.addParameter(new PluginParameter({
      id: 'pan',
      name: 'Pan',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 0,
      minValue: -1,
      maxValue: 1,
      displayValue: (v) => {
        if (Math.abs(v) < 0.05) return 'C';
        return v < 0 ? `L${Math.round(Math.abs(v) * 100)}` : `R${Math.round(v * 100)}`;
      },
    }));

    this.addParameter(new PluginParameter({
      id: 'phase',
      name: 'Phase Invert',
      type: PARAM_TYPES.BOOL,
      defaultValue: false,
    }));
  }

  onParameterChange(paramId, value) {
    const currentTime = this.audioContext.currentTime;
    switch (paramId) {
      case 'gain':
        const linearGain = Math.pow(10, value / 20);
        const phase = this.getParameter('phase').getValue();
        this.gainNode.gain.setValueAtTime(phase ? -linearGain : linearGain, currentTime);
        break;
      case 'pan':
        this.panner.pan.setValueAtTime(value, currentTime);
        break;
      case 'phase':
        const gain = this.getParameter('gain').getValue();
        const linGain = Math.pow(10, gain / 20);
        this.gainNode.gain.setValueAtTime(value ? -linGain : linGain, currentTime);
        break;
      default:
        break;
    }
  }

  _updateBypass() {
    const currentTime = this.audioContext.currentTime;
    if (this.enabled) {
      this.bypassNode.gain.setValueAtTime(0, currentTime);
      const gain = this.getParameter('gain').getValue();
      const phase = this.getParameter('phase').getValue();
      const linearGain = Math.pow(10, gain / 20);
      this.gainNode.gain.setValueAtTime(phase ? -linearGain : linearGain, currentTime);
    } else {
      this.bypassNode.gain.setValueAtTime(1, currentTime);
      this.gainNode.gain.setValueAtTime(0, currentTime);
    }
  }
}

// ==================== REGISTER ALL PLUGINS ====================

// Track if plugins have been registered to avoid duplicates
let pluginsRegistered = false;

export function registerBuiltInPlugins() {
  // Prevent duplicate registration
  if (pluginsRegistered) {
    return;
  }
  
  pluginRegistry.registerPlugin('compressor', CompressorPlugin, {
    type: PLUGIN_TYPES.EFFECT,
    category: PLUGIN_CATEGORIES.DYNAMICS,
    description: 'Dynamic range compressor',
    icon: 'activity',
  });

  pluginRegistry.registerPlugin('parametric-eq', ParametricEQPlugin, {
    type: PLUGIN_TYPES.EFFECT,
    category: PLUGIN_CATEGORIES.EQ,
    description: '4-band parametric EQ',
    icon: 'sliders',
  });

  pluginRegistry.registerPlugin('reverb', ReverbPlugin, {
    type: PLUGIN_TYPES.EFFECT,
    category: PLUGIN_CATEGORIES.REVERB,
    description: 'Algorithmic reverb',
    icon: 'waves',
  });

  pluginRegistry.registerPlugin('delay', DelayPlugin, {
    type: PLUGIN_TYPES.EFFECT,
    category: PLUGIN_CATEGORIES.DELAY,
    description: 'Stereo delay with ping-pong',
    icon: 'timer',
  });

  pluginRegistry.registerPlugin('chorus', ChorusPlugin, {
    type: PLUGIN_TYPES.EFFECT,
    category: PLUGIN_CATEGORIES.MODULATION,
    description: 'Stereo chorus',
    icon: 'music2',
  });

  pluginRegistry.registerPlugin('gain', GainPlugin, {
    type: PLUGIN_TYPES.EFFECT,
    category: PLUGIN_CATEGORIES.UTILITY,
    description: 'Gain, pan, phase',
    icon: 'volume2',
  });
  
  pluginsRegistered = true;
}

// Auto-register plugins when module is loaded
registerBuiltInPlugins();

// Export all plugins
export default {
  CompressorPlugin,
  ParametricEQPlugin,
  ReverbPlugin,
  DelayPlugin,
  ChorusPlugin,
  GainPlugin,
  registerBuiltInPlugins,
};
